import { flexRender, Header, Row } from "@tanstack/react-table"; // Import Row and Cell types
import clsx from "clsx";

import {
  DataGridLoader,
  DataGridPagination,
  DataGridTableBodyRowSelect,
  DataGridTableHeadCell,
} from "./components"; // Ensure these imports are correct
import { useDataGrid } from "./DataGridProvider";
import { DataTableProps } from "./DataTable";

export function DataGridInner<TData extends object>({
  onRowClick,
  columns,
  title,
  searchPlaceholder,
  header,
  showHeadRow,
  footer,
  enableSearch,
  enablePagination,
  isInfoIcon,
}: DataTableProps<TData>) {
  const { loading, table, props, toggleAllRowsSelection, isSelectAllChecked } =
    useDataGrid();

  return (
    <>
      {(title || enableSearch || isInfoIcon || header) && (
        <div className="card-header py-5 flex-wrap justify-between">
          <div className="flex flex-row gap-10 items-center">
            {title && <h3 className="card-title">{title}</h3>}
            <div className="flex flex-row gap-10">
              {enableSearch && (
                <div className="input h-8">
                  <i className="ki-filled ki-magnifier"></i>
                  <input
                    className="min-w-60"
                    placeholder={searchPlaceholder}
                    type="text"
                    value={table.getState().globalFilter ?? ""}
                    onChange={(e) => table.setGlobalFilter(e.target.value)}
                  />
                </div>
              )}
              {isInfoIcon && (
                <i className="ki-outline ki-information-3 text-[#8D4F61] text-1.5xl" />
              )}
            </div>
          </div>

          {header}
        </div>
      )}
      <div className="grid min-w-full">
        <div className="scrollable-x-auto">
          <table
            className={clsx(
              "table table-fixed",
              props.layout?.cellsBorder && "table-border",
              props.layout?.tableSpacing &&
                {
                  xs: "table-xs",
                  sm: "table-sm",
                  lg: "table-lg",
                }[props.layout.tableSpacing],
            )}
          >
            {showHeadRow && (
              <thead>
                <tr>
                  {props.rowSelect && (
                    <th
                      style={{
                        width: 50,
                      }}
                    >
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        onChange={(e) => {
                          toggleAllRowsSelection(e.target.checked);
                        }}
                        checked={isSelectAllChecked}
                      />
                    </th>
                  )}
                  {table
                    .getHeaderGroups()
                    .map((headerGroup: { headers: Header<TData, unknown>[] }) =>
                      headerGroup.headers.map((header, index) => (
                        <DataGridTableHeadCell
                          key={index}
                          header={header}
                          column={columns[header.index]}
                        />
                      )),
                    )}
                </tr>
              </thead>
            )}
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table
                  .getRowModel()
                  .rows.map((row: Row<TData>, rowIndex: number) => (
                    <tr
                      onClick={() => onRowClick?.(row.original)}
                      key={rowIndex}
                      id={row.id}
                    >
                      {props.rowSelect && (
                        <DataGridTableBodyRowSelect data={row.original} />
                      )}
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
              ) : (
                // Show empty message if no data
                <tr>
                  <td
                    colSpan={
                      table.getAllColumns().length + (props.rowSelect ? 1 : 0)
                    }
                    className="text-center py-4"
                  >
                    {props.messages?.empty || "No data available"}
                  </td>
                </tr>
              )}
              {footer}
            </tbody>
          </table>
          {loading && <DataGridLoader />} {/* Show loader if loading */}
        </div>
        {enablePagination && (
          <div className="card-footer justify-center md:justify-between flex-col md:flex-row gap-3 text-gray-600 text-2sm font-medium">
            <div className="flex items-center gap-2">
              {props.pagination?.sizesLabel}
              <select
                className="select select-sm w-16"
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
              >
                {props.pagination?.sizes?.map((size, index) => (
                  <option key={index} value={size}>
                    {size}
                  </option>
                ))}
              </select>{" "}
              {props.pagination?.sizesDescription}
            </div>
            <DataGridPagination />
          </div>
        )}
      </div>
    </>
  );
}
